import React, { useEffect, useMemo, useRef, useState } from 'react';

interface DashboardEmailFilterProps {
  ariaLabel: string;
  label?: string;
  noun?: string;
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
}

export const DashboardEmailFilter: React.FC<DashboardEmailFilterProps> = ({
  ariaLabel,
  label = 'Updated by',
  noun = 'users',
  options,
  selected,
  onChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [filterInput, setFilterInput] = useState('');
  const normalizedOptions = useMemo(
    () => [...new Set([...selected, ...options].filter(Boolean))].sort(),
    [options, selected]
  );
  const normalizedFilter = filterInput.trim().toLowerCase();
  const filteredOptions = useMemo(
    () =>
      normalizedFilter
        ? normalizedOptions.filter((option) => option.toLowerCase().includes(normalizedFilter))
        : normalizedOptions,
    [normalizedFilter, normalizedOptions]
  );

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const buttonText =
    selected.length === 0
      ? `All ${noun}`
      : selected.length === 1
        ? selected[0]
        : `${selected.length} ${noun}`;

  const toggleOption = (option: string) => {
    const next = new Set(selectedSet);
    if (next.has(option)) {
      next.delete(option);
    } else {
      next.add(option);
    }
    onChange([...next].sort());
  };

  const selectFilteredOptions = () => {
    const next = new Set(selectedSet);
    filteredOptions.forEach((option) => next.add(option));
    onChange([...next].sort());
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && !containerRef.current?.contains(target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) setFilterInput('');
  }, [isOpen]);

  return (
    <div className="dg-email-filter" ref={containerRef}>
      <button
        type="button"
        className="dg-email-filter-button"
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
      >
        <span>{label}</span>
        <strong>{buttonText}</strong>
      </button>
      {isOpen ? (
        <div className="dg-email-filter-menu" role="menu">
          <button type="button" className="dg-email-filter-action" onClick={() => onChange([])}>
            All {noun}
          </button>
          <input
            type="search"
            className="dg-email-filter-search"
            aria-label={`${ariaLabel} prefilter`}
            placeholder={`Prefilter ${noun}`}
            value={filterInput}
            onChange={(event) => setFilterInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
              }
            }}
          />
          <button
            type="button"
            className="dg-email-filter-action"
            disabled={filteredOptions.length === 0}
            onClick={selectFilteredOptions}
          >
            Select matching
          </button>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <label key={option} className="dg-email-filter-option">
                <input
                  type="checkbox"
                  checked={selectedSet.has(option)}
                  onChange={() => toggleOption(option)}
                />
                <span title={option}>{option}</span>
              </label>
            ))
          ) : (
            <div className="dg-email-filter-empty">No {noun}</div>
          )}
        </div>
      ) : null}
    </div>
  );
};
